import axios, {
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import { useAuthStore } from '@src/core/store/auth-store';
import { getCurrentOrgSlug } from '@src/shared/utils/get-org-id';
import { fetchRefreshToken } from '@src/core/api/fetch-refresh-token';
import { assistantScope } from '../hooks/use-assistant-scope';

type ScopedConfig = InternalAxiosRequestConfig & { assistantScope?: string };

// The assistant lives inside the CRM API (apps/ai_assistant, mounted at
// /api/v1/crm/assistant/), not on a separate service — same host, same auth.
// ponytail: still its own axios instance rather than folded into apiClient
// only because apiClient rejects with a plain string, and these services and
// their views rely on Error.message. Merge them if that ever changes.
export const assistantClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_CRM_APP_API_URL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

assistantClient.interceptors.request.use((config) => {
  const scoped = config as ScopedConfig;
  const currentScope = assistantScope();
  if (scoped.assistantScope && scoped.assistantScope !== currentScope) {
    throw new Error(
      'The assistant session changed. Open the conversation again.'
    );
  }
  scoped.assistantScope = currentScope;
  const { accessToken } = useAuthStore.getState();
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  const org = getCurrentOrgSlug();
  if (org) {
    config.params = { ...(config.params || {}), organization: org };
  }
  return config;
});

// Mirror the CRM responseErrorInterceptor: 401 → refresh → retry once.
assistantClient.interceptors.response.use(
  (response) => {
    if ((response.config as ScopedConfig).assistantScope !== assistantScope()) {
      throw new Error(
        'The assistant session changed. Open the conversation again.'
      );
    }
    return response;
  },
  async (error) => {
    const original = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (error.response?.status === 401 && original && !original._retry) {
      original._retry = true;

      const fresh = await fetchRefreshToken();
      if (fresh) {
        original.headers.Authorization = `Bearer ${fresh}`;
        return assistantClient(original);
      }
    }

    return Promise.reject(error);
  }
);

/** DRF PagePaginator envelope (core/pagination.py). Pages are 1-indexed. */
export interface Page<T> {
  count: number;
  page_number: number;
  total_pages: number;
  results: T[];
}

/** `getNextPageParam` for react-query, over the DRF envelope. */
export function nextPage(page: Page<unknown>): number | undefined {
  return page.page_number < page.total_pages ? page.page_number + 1 : undefined;
}

/** The paginator's page query parameter (`PagePaginator.page_query_param`). */
export const pageParams = (page: number) => ({ page_number: page });
